import { FC, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {
  HotelFilters as HotelFiltersType,
  HotelSortOption,
  HotelBoardType,
  HOTEL_RATING_OPTIONS,
  HOTEL_BOARD_OPTIONS,
  HOTEL_SORT_OPTIONS,
} from '../../../../../types/hotel.types';

import styles from './HotelFilters.module.scss';

interface HotelFiltersProps {
  filters: HotelFiltersType;
  onFiltersChange: (filters: HotelFiltersType) => void;
}

type FilterSectionKey = 'sort' | 'rating' | 'additionally';

const INITIAL_SECTION_OPEN: Record<FilterSectionKey, boolean> = {
  sort: true,
  rating: true,
  additionally: true,
};

const FilterCheckboxUnchecked = () => <span className={styles.filterCheckboxUnchecked} aria-hidden />;

const FilterCheckboxChecked = () => (
  <span className={styles.filterCheckboxChecked} aria-hidden>
    <span className={styles.filterCheckboxCheckmark} />
  </span>
);

const HotelFilters: FC<HotelFiltersProps> = ({ filters, onFiltersChange }) => {
  const [openSections, setOpenSections] = useState<Record<FilterSectionKey, boolean>>(INITIAL_SECTION_OPEN);

  const toggleSection = (key: FilterSectionKey) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sectionHeader = (key: FilterSectionKey, title: string) => (
    <Box
      className={`${styles.filterHeader} ${openSections[key] ? styles.filterHeaderExpanded : ''}`}
      onClick={() => toggleSection(key)}
      role="button"
      tabIndex={0}
      aria-expanded={openSections[key]}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSection(key);
        }
      }}
    >
      <Typography
        component="span"
        className={styles.filterTitle}
      >
        {title}
      </Typography>
      <KeyboardArrowDownIcon
        className={`${styles.expandIcon} ${openSections[key] ? styles.expandIconOpen : ''}`}
        aria-hidden
      />
    </Box>
  );

  const handleSortChange = (event: any) => {
    onFiltersChange({
      ...filters,
      sortBy: event.target.value as HotelSortOption,
    });
  };

  const handleRatingToggle = (rating: number) => {
    const next = filters.ratings.includes(rating)
      ? filters.ratings.filter(r => r !== rating)
      : [...filters.ratings, rating];
    onFiltersChange({
      ...filters,
      ratings: next,
    });
  };

  const handleBoardToggle = (board: HotelBoardType) => {
    const next = filters.boards.includes(board)
      ? filters.boards.filter(b => b !== board)
      : [...filters.boards, board];
    onFiltersChange({
      ...filters,
      boards: next,
    });
  };

  return (
    <Box className={styles.filtersContainer}>
      <Box className={styles.filterSection}>
        {sectionHeader('sort', 'Sort By')}
        <Collapse
          in={openSections.sort}
          timeout={280}
          unmountOnExit={false}
        >
          <FormControl
            fullWidth
            onClick={e => e.stopPropagation()}
          >
            <Select
              value={filters.sortBy}
              onChange={handleSortChange}
              className={styles.select}
              MenuProps={{
                classes: { paper: styles.selectMenu },
              }}
            >
              {HOTEL_SORT_OPTIONS.map(option => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className={styles.menuItem}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('rating', 'Rating')}
        <Collapse
          in={openSections.rating}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            {HOTEL_RATING_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                className={styles.checkboxRow}
                control={
                  <Checkbox
                    disableRipple
                    checked={filters.ratings.includes(option.value)}
                    onChange={() => handleRatingToggle(option.value)}
                    className={styles.filterCheckbox}
                    size="small"
                    icon={<FilterCheckboxUnchecked />}
                    checkedIcon={<FilterCheckboxChecked />}
                  />
                }
                label={<span className={styles.checkboxLabel}>{option.label}</span>}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('additionally', 'Additionally')}
        <Collapse
          in={openSections.additionally}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            {HOTEL_BOARD_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                className={styles.checkboxRow}
                control={
                  <Checkbox
                    disableRipple
                    checked={filters.boards.includes(option.value)}
                    onChange={() => handleBoardToggle(option.value)}
                    className={styles.filterCheckbox}
                    size="small"
                    icon={<FilterCheckboxUnchecked />}
                    checkedIcon={<FilterCheckboxChecked />}
                  />
                }
                label={<span className={styles.checkboxLabel}>{option.label}</span>}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default HotelFilters;
